import type {
  KnowledgeSourceSearchResponse,
  KnowledgeSourceSearchParams,
  KnowledgeCatalogListResponse,
  KnowledgeCatalogListParams,
  KnowledgeCatalog,
} from '@kizen/api/knowledge';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithParams, WithRequired } from './types';

import {
  getKnowledgeSourceSearch,
  OnlineSourceSearchResponse,
  OnlineSourceSearchParams,
  getOnlineSourceSearch,
  OnlineSourcePagesSearchResponse,
  getOnlineSourcePagesSearch,
  OnlineSourcePagesSearchParams,
  getKnowledgeCatalogs,
  getKnowledgeCatalogById,
} from '@kizen/api/knowledge';
import { getResource } from './resource';
import { curry1 } from './utils';

type KnowledgeSourceSearchQuery<TData = KnowledgeSourceSearchResponse> =
  WithRequired<
    QueryObserverOptions<
      KnowledgeSourceSearchResponse,
      DefaultError,
      TData,
      KnowledgeSourceSearchResponse,
      [
        Resource<
          Readonly<['knowledge', 'source', 'search']>,
          KnowledgeSourceSearchParams
        >,
      ]
    >,
    'queryFn' | 'queryKey'
  >;

type OnlineSourceSearchQuery<TData = OnlineSourceSearchResponse> = WithRequired<
  QueryObserverOptions<
    OnlineSourceSearchResponse,
    DefaultError,
    TData,
    OnlineSourceSearchResponse,
    [
      Resource<
        Readonly<['online', 'source', 'search']>,
        OnlineSourceSearchParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type OnlineSourcePagesSearchQuery<TData = OnlineSourcePagesSearchResponse> =
  WithRequired<
    QueryObserverOptions<
      OnlineSourcePagesSearchResponse,
      DefaultError,
      TData,
      OnlineSourcePagesSearchResponse,
      [
        Resource<
          Readonly<['online', 'source', 'pages', 'search']>,
          OnlineSourcePagesSearchParams
        >,
      ]
    >,
    'queryFn' | 'queryKey'
  >;

export const knowledgeSourceSearchQuery = <
  TData = KnowledgeSourceSearchResponse,
>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<KnowledgeSourceSearchQuery<TData>, 'queryFn' | 'queryKey'>,
    KnowledgeSourceSearchParams
  > = {}
): KnowledgeSourceSearchQuery<TData> => {
  const path = ['knowledge', 'source', 'search'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getKnowledgeSourceSearch(axios, {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const onlineSourceSearchQuery = <TData = OnlineSourceSearchResponse>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<OnlineSourceSearchQuery<TData>, 'queryFn' | 'queryKey'>,
    OnlineSourceSearchParams
  > = {}
): OnlineSourceSearchQuery<TData> => {
  const path = ['online', 'source', 'search'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getOnlineSourceSearch(axios, {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const onlineSourcePagesSearchQuery = <
  TData = OnlineSourcePagesSearchResponse,
>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<OnlineSourcePagesSearchQuery<TData>, 'queryFn' | 'queryKey'>,
    OnlineSourcePagesSearchParams
  > = {}
): OnlineSourcePagesSearchQuery<TData> => {
  const path = ['online', 'source', 'pages', 'search'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getOnlineSourcePagesSearch(axios, {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

type KnowledgeCatalogListQuery<TData = KnowledgeCatalogListResponse> =
  WithRequired<
    QueryObserverOptions<
      KnowledgeCatalogListResponse,
      DefaultError,
      TData,
      KnowledgeCatalogListResponse,
      [
        Resource<
          Readonly<['knowledge', 'catalogs']>,
          KnowledgeCatalogListParams
        >,
      ]
    >,
    'queryFn' | 'queryKey'
  >;

type KnowledgeCatalogQuery<TData = KnowledgeCatalog> = WithRequired<
  QueryObserverOptions<
    KnowledgeCatalog,
    DefaultError,
    TData,
    KnowledgeCatalog,
    [Resource<Readonly<['knowledge', 'catalog', string]>, undefined>]
  >,
  'queryFn' | 'queryKey'
>;

export const knowledgeCatalogListQuery = <TData = KnowledgeCatalogListResponse>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<KnowledgeCatalogListQuery<TData>, 'queryFn' | 'queryKey'>,
    KnowledgeCatalogListParams
  > = {}
): KnowledgeCatalogListQuery<TData> => {
  const path = ['knowledge', 'catalogs'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getKnowledgeCatalogs(axios, {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const knowledgeCatalogQuery = <TData = KnowledgeCatalog>(
  axios: AxiosInstance,
  id: string,
  options: Omit<KnowledgeCatalogQuery<TData>, 'queryFn' | 'queryKey'> = {}
): KnowledgeCatalogQuery<TData> => {
  const path = ['knowledge', 'catalog', id] as const;
  const resource = getResource(path, {});

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getKnowledgeCatalogById(axios, id, { signal });
    },
    ...options,
  };
};

class Queries {
  public knowledgeSourceSearchQuery: <TData = KnowledgeSourceSearchResponse>(
    ...args: Drop<Parameters<typeof knowledgeSourceSearchQuery<TData>>>
  ) => KnowledgeSourceSearchQuery<TData>;

  public onlineSourceSearchQuery: <TData = OnlineSourceSearchResponse>(
    ...args: Drop<Parameters<typeof onlineSourceSearchQuery<TData>>>
  ) => OnlineSourceSearchQuery<TData>;

  public onlineSourcePagesSearchQuery: <
    TData = OnlineSourcePagesSearchResponse,
  >(
    ...args: Drop<Parameters<typeof onlineSourcePagesSearchQuery<TData>>>
  ) => OnlineSourcePagesSearchQuery<TData>;

  public knowledgeCatalogListQuery: <TData = KnowledgeCatalogListResponse>(
    ...args: Drop<Parameters<typeof knowledgeCatalogListQuery<TData>>>
  ) => KnowledgeCatalogListQuery<TData>;

  public knowledgeCatalogQuery: <TData = KnowledgeCatalog>(
    id: string,
    options?: Omit<KnowledgeCatalogQuery<TData>, 'queryFn' | 'queryKey'>
  ) => KnowledgeCatalogQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.knowledgeSourceSearchQuery = curry1(knowledgeSourceSearchQuery)(axios);
    this.onlineSourceSearchQuery = curry1(onlineSourceSearchQuery)(axios);
    this.onlineSourcePagesSearchQuery = curry1(onlineSourcePagesSearchQuery)(
      axios
    );
    this.knowledgeCatalogListQuery = curry1(knowledgeCatalogListQuery)(axios);
    this.knowledgeCatalogQuery = (id, options) =>
      knowledgeCatalogQuery(axios, id, options);
  }
}

export type KnowledgeQueries = Queries;

export const defKnowledgeQueries = (axios: AxiosInstance): KnowledgeQueries => {
  return new Queries(axios);
};
